import { FC } from 'react';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import Input from '@components/common/Input';
import MaskedInput from '@components/common/MaskedInput';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmailValid, isPhoneNumber } from '@utils/validators';
import { FieldErrors } from 'react-hook-form/dist/types/errors';

export interface IPayeeInfoFormProps {
  onSuccess?: (result: unknown) => void;
  onError?: (error?: unknown) => void;
  onValidationError?: (errors: FieldErrors) => void;
}

const PayeeInfoForm: FC<IPayeeInfoFormProps> = () => {
  const { control, formState } = useFormContext();
  const css = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <Grid className={css.formRow}>
        <Controller
          name="payeeFirstName"
          defaultValue=""
          rules={{ required: true }}
          control={control}
          render={({ field: { ref, ...field }, fieldState }) => (
            <Input
              {...field}
              inputRef={ref}
              data-testid="payee-name"
              label={t('checkoutScreen.payment.info.name')}
              fullWidth
              autoComplete="off"
              disabled={formState.isSubmitting}
              error={fieldState.invalid}
            />
          )}
        />
      </Grid>
      <Grid className={css.formRow}>
        <Controller
          control={control}
          defaultValue=""
          name="payeeLastName"
          rules={{ required: true }}
          render={({ field: { ref, ...field }, fieldState }) => (
            <Input
              {...field}
              inputRef={ref}
              data-testid="payee-lastname"
              label={t('checkoutScreen.payment.info.lastName')}
              fullWidth
              autoComplete="off"
              disabled={formState.isSubmitting}
              error={fieldState.invalid}
            />
          )}
        />
      </Grid>
      <Grid className={css.formRow}>
        <Controller
          name="payeeEmail"
          defaultValue=""
          control={control}
          rules={{ required: true, validate: { email: isEmailValid } }}
          render={({ field: { ref, ...field }, fieldState }) => (
            <Input
              {...field}
              inputRef={ref}
              data-testid="payee-email"
              label={t('checkoutScreen.payment.info.email')}
              fullWidth
              autoComplete="off"
              disabled={formState.isSubmitting}
              error={fieldState.invalid}
            />
          )}
        />
      </Grid>
      <Grid className={css.formRow}>
        <Controller
          name="payeePhone"
          control={control}
          defaultValue=""
          rules={{ required: true, validate: { phone: isPhoneNumber } }}
          render={({ field: { ref, ...field }, fieldState }) => (
            <MaskedInput
              {...field}
              inputRef={ref}
              data-testid="payee-phone"
              label={t('checkoutScreen.payment.info.phone')}
              placeholder={t('checkoutScreen.payment.info.phonePlaceholder')}
              autoComplete="off"
              fullWidth
              inputProps={{
                inputMode: 'numeric',
                mask: '+52 99 9999 9999',
              }}
              disabled={formState.isSubmitting}
              error={fieldState.invalid}
            />
          )}
        />
      </Grid>
    </>
  );
};

export default observer(PayeeInfoForm);
